import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useToast } from '../ui/ui';
import { formatMoney } from './format';

export interface Page<T> {
  data: T[];
  pageInfo: { nextCursor: string | null; hasMore: boolean };
}

/** Listado paginado por cursor ("Cargar más"). */
export function usePaged<T>(key: QueryKey, fetchPage: (cursor?: string) => Promise<Page<T>>) {
  const query = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.pageInfo.nextCursor ?? undefined,
  });
  return {
    rows: query.data?.pages.flatMap((p) => p.data) ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
  };
}

/** Formateador de dinero con la moneda de la empresa. */
export function useMoney(): (value: string | number | null | undefined) => string {
  const { me } = useAuth();
  const currency = me?.tenant.currency ?? 'COP';
  return useCallback((value) => formatMoney(value, currency), [currency]);
}

/** Mutación con invalidación de consultas y toasts de éxito/error. */
export function useAction<TInput, TResult>(
  action: (input: TInput) => Promise<TResult>,
  options: {
    success?: string | ((result: TResult) => string);
    invalidate?: QueryKey[];
    onSuccess?: (result: TResult) => void;
  } = {},
) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: action,
    onSuccess: async (result) => {
      await Promise.all(
        (options.invalidate ?? []).map((key) => queryClient.invalidateQueries({ queryKey: key })),
      );
      if (options.success)
        toast.notify(
          typeof options.success === 'function' ? options.success(result) : options.success,
        );
      options.onSuccess?.(result);
    },
    onError: (error) => toast.fail(error),
  });
}

/** Valor con retardo para búsquedas mientras se escribe. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function toQuery<T extends Record<string, unknown>>(filters: T): T {
  return Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== '' && v !== undefined && v !== null),
  ) as T;
}
